import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { CheckCircle, Edit, Eye, FileText } from "lucide-react";
import { formatNZDate } from "../../utils/timezone";

const STATUS_COLORS = {
  active: "bg-green-500/10 text-green-600 border-green-500/30",
  accepted: "bg-green-500/10 text-green-600 border-green-500/30",
  submitted: "bg-blue-500/10 text-blue-600 border-blue-500/30",
  pending: "bg-yellow-500/10 text-yellow-600 border-yellow-500/30",
  paid: "bg-green-500/10 text-green-600 border-green-500/30",
  confirmed: "bg-blue-500/10 text-blue-600 border-blue-500/30",
  completed: "bg-blue-500/10 text-blue-600 border-blue-500/30",
  expired: "bg-gray-500/10 text-gray-600 border-gray-500/30",
  rejected: "bg-red-500/10 text-red-600 border-red-500/30",
  pending_fitment: "bg-orange-500/10 text-orange-600 border-orange-500/30",
};

/**
 * Groups submitted quotes by request_id and renders one Card per request,
 * with a row of compact per-quote tiles inside. Mirrors the customer view's
 * pricing layout (per-tyre excl GST hero) but in a compact horizontal layout
 * so a shop can see 1–5 quotes for the same request at a glance.
 *
 * Hands the original `onView` / `isQuoteEditable` props through unchanged so
 * the View / View-Edit window logic from ShopDashboard is preserved.
 */
export function ShopMyQuotesGroupedList({ quotes, onView, isQuoteEditable }) {
  // Group preserves first-seen order (which is fetchData's sort: most recent
  // submission first).
  const groups = [];
  const indexByRequest = new Map();
  for (const q of quotes) {
    const key = q.request_id || q.request_number;
    if (!indexByRequest.has(key)) {
      indexByRequest.set(key, groups.length);
      groups.push({
        request_id: q.request_id,
        request_number: q.request_number,
        quotes: [],
      });
    }
    groups[indexByRequest.get(key)].quotes.push(q);
  }

  return (
    <div className="space-y-4" data-testid="my-quotes-grouped-list">
      {groups.map((g) => (
        <RequestGroupCard
          key={g.request_id || g.request_number}
          group={g}
          onView={onView}
          isQuoteEditable={isQuoteEditable}
        />
      ))}
    </div>
  );
}

function RequestGroupCard({ group, onView, isQuoteEditable }) {
  const count = group.quotes.length;
  const hasSelected = group.quotes.some((q) => q.is_selected);
  const earliestSubmitted = group.quotes
    .map((q) => q.created_at)
    .filter(Boolean)
    .sort()[0];
  // request_info is attached by /shop/my-quotes; same on every quote in the
  // group — pick from the first one.
  const reqInfo = group.quotes[0]?.request_info || {};
  const veh = reqInfo.vehicle_info || {};
  const vehicleLine = [veh.year, veh.make, veh.model]
    .filter(Boolean)
    .join(" ");

  return (
    <Card
      className="overflow-hidden"
      data-testid={`request-group-${group.request_id || group.request_number}`}
    >
      {/* Request header strip */}
      <div className="px-5 py-3 border-b bg-muted/40 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
          <h3 className="font-heading text-base font-semibold truncate">
            Request {group.request_number || `#${group.request_id}`}
          </h3>
          <Badge
            variant="outline"
            className="text-[10px] font-medium"
            data-testid={`group-quote-count-${group.request_id || group.request_number}`}
          >
            {count} {count === 1 ? "quote" : "quotes"} submitted
          </Badge>
          {hasSelected && (
            <Badge className="bg-green-500/10 text-green-600 border-green-500/30 text-[10px]">
              <CheckCircle className="h-3 w-3 mr-1" />
              Selected
            </Badge>
          )}
          {(veh.rego || vehicleLine) && (
            <span
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
              data-testid={`group-vehicle-${group.request_id || group.request_number}`}
            >
              <span className="text-muted-foreground">·</span>
              {veh.rego && (
                <span className="font-semibold tracking-wide uppercase text-foreground">
                  {veh.rego}
                </span>
              )}
              {vehicleLine && (
                <span className="font-medium text-foreground">
                  {vehicleLine}
                </span>
              )}
              {veh.sub_model && (
                <span className="text-muted-foreground">· {veh.sub_model}</span>
              )}
              {veh.lookup_source === "carjam" && (
                <span className="inline-block px-1.5 py-0 rounded text-[9px] font-bold tracking-wider bg-green-500/15 text-green-700 dark:text-green-300 border border-green-500/40">
                  CARJAM
                </span>
              )}
            </span>
          )}
        </div>
        {earliestSubmitted && (
          <p className="text-[11px] text-muted-foreground shrink-0">
            First submitted {formatNZDate(earliestSubmitted)}
          </p>
        )}
      </div>

      {/* Quote tiles row (horizontal scroll on overflow) */}
      <CardContent className="p-3 sm:p-4">
        <div
          className="flex gap-3 overflow-x-auto pb-1 snap-x snap-mandatory md:snap-none"
          data-testid={`quote-tiles-${group.request_id || group.request_number}`}
        >
          {group.quotes.map((q, i) => (
            <QuoteTile
              key={q.quote_id}
              quote={q}
              optionNumber={i + 1}
              onView={onView}
              isEditable={isQuoteEditable(q.created_at)}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ---- Compact per-quote tile (1 of N inside a request group) ----
function QuoteTile({ quote, optionNumber, onView, isEditable }) {
  const pb = quote.price_breakdown || {};
  const factor = pb.prices_include_gst ? 1 : 1.15;
  const tyreFitInclGst = (pb.tyre_fitment || pb.tyres || 0) * factor;
  const alignmentInclGst = (pb.alignment || 0) * factor;
  const rotationInclGst = (pb.rotation || 0) * factor;
  const totalInclGst = quote.total_price || quote.price || 0;
  // Quantity authoritative on the parent REQUEST, not the quote. The
  // `/api/shop/my-quotes` endpoint attaches the request as
  // `quote.request_info`, so we pull from there. Fallback chain handles
  // edge cases (old quotes pre-multi-quote era and non-tyre services).
  const qty =
    quote.request_info?.quantity ||
    quote.quantity ||
    pb.quantity ||
    4;
  const isTyre = tyreFitInclGst > 0;
  const perInclGst = isTyre && qty > 0 ? tyreFitInclGst / qty : 0;
  const perExclGst = perInclGst / 1.15;

  const stateBorder = quote.is_selected
    ? "border-green-500/60 ring-1 ring-green-500/30"
    : "border-input";

  return (
    <div
      className={`snap-center shrink-0 w-[260px] rounded-lg border-2 ${stateBorder} bg-card p-3 flex flex-col gap-2`}
      data-testid={`quote-tile-${quote.quote_id}`}
    >
      {/* Tile header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] font-bold grid place-items-center shrink-0">
            {optionNumber}
          </span>
          <span className="text-xs font-medium text-muted-foreground truncate">
            Option {optionNumber}
          </span>
        </div>
        <Badge
          className={`${STATUS_COLORS[quote.status] || STATUS_COLORS.submitted} text-[9px] px-1.5 py-0`}
        >
          {quote.status}
        </Badge>
      </div>

      {/* Hero pricing */}
      <div className="border-t pt-2">
        {isTyre ? (
          <>
            <p
              className="text-2xl font-heading font-bold text-primary leading-none"
              data-testid={`tile-per-tyre-excl-gst-${quote.quote_id}`}
            >
              ${perExclGst.toFixed(2)}
            </p>
            <p className="text-[9px] text-muted-foreground">
              excl. GST per tyre fitted
            </p>
            <p className="text-xs font-medium text-foreground/80 mt-0.5">
              ${perInclGst.toFixed(2)}{" "}
              <span className="text-[9px] text-muted-foreground font-normal">
                incl. GST
              </span>
            </p>
          </>
        ) : (
          <>
            <p className="text-2xl font-heading font-bold text-primary leading-none">
              ${totalInclGst.toFixed(2)}
            </p>
            <p className="text-[9px] text-muted-foreground">incl. GST total</p>
          </>
        )}
      </div>

      {/* Tyre details */}
      {isTyre && (quote.tyre_brand || quote.tyre_model) && (
        <div className="text-[11px] border-t pt-1.5 leading-tight">
          <p className="font-medium text-foreground truncate">
            {quote.tyre_brand} {quote.tyre_model}
          </p>
          <p className="text-muted-foreground truncate">
            {quote.tyre_size && <>{quote.tyre_size} • </>}
            {(quote.stock_availability || "in_stock").replace(/_/g, " ")}
          </p>
        </div>
      )}

      {/* Compact breakdown */}
      <div className="text-[10px] text-muted-foreground border-t pt-1.5 space-y-0.5">
        {tyreFitInclGst > 0 && (
          <div className="flex justify-between">
            <span>Fitment</span>
            <span>${tyreFitInclGst.toFixed(0)}</span>
          </div>
        )}
        {alignmentInclGst > 0 && (
          <div className="flex justify-between">
            <span>Alignment</span>
            <span>${alignmentInclGst.toFixed(0)}</span>
          </div>
        )}
        {rotationInclGst > 0 && (
          <div className="flex justify-between">
            <span>Rotation</span>
            <span>${rotationInclGst.toFixed(0)}</span>
          </div>
        )}
        <div className="flex justify-between font-semibold text-foreground pt-0.5 border-t">
          <span>Total</span>
          <span data-testid={`tile-total-${quote.quote_id}`}>
            ${totalInclGst.toFixed(2)}
          </span>
        </div>
      </div>

      {/* Order/extra status row */}
      {(quote.order_status || quote.is_selected) && (
        <div className="flex flex-wrap gap-1">
          {quote.is_selected && (
            <Badge className="bg-green-500/10 text-green-600 border-green-500/30 text-[9px] px-1 py-0">
              <CheckCircle className="h-2.5 w-2.5 mr-0.5" />
              Selected
            </Badge>
          )}
          {quote.order_status && (
            <Badge
              className={`${STATUS_COLORS[quote.order_status] || STATUS_COLORS.submitted} text-[9px] px-1 py-0`}
            >
              Order: {quote.order_status}
            </Badge>
          )}
        </div>
      )}

      {/* Expiry */}
      {quote.quote_expiry && (
        <p className="text-[10px] text-muted-foreground">
          Valid until {formatNZDate(quote.quote_expiry)}
        </p>
      )}

      {/* Action */}
      <Button
        variant="outline"
        size="sm"
        className="mt-auto h-7 text-xs"
        onClick={() => onView(quote)}
        data-testid={`view-quote-${quote.quote_id}`}
      >
        {isEditable ? (
          <>
            <Edit className="h-3 w-3 mr-1" />
            View / Edit
          </>
        ) : (
          <>
            <Eye className="h-3 w-3 mr-1" />
            View
          </>
        )}
      </Button>
    </div>
  );
}
